import { QueryClient } from "@tanstack/react-query";
import { Node } from "reactflow";
import ApiService from "services/api.service";
import { AiAgent } from "types/AiAgent";
import Template from "types/Template";
import { MessageType } from "types/Workflow";
import { InternalRecipientGroup } from "types/InternalRecipient";
import { LeadStatsMergeField } from "types/LeadStatsMergeField";
import { DrawerAction } from "./Drawer/drawer.fixtures";
import { NodeType } from "./FlowEditor";
import { MessageNodeData, NodeData } from "./Nodes/NodeData";

// Shared React Query definitions for the data the workflow-builder side panels
// need. Two things use these:
//   1. The settings panels read through `ensureQueryData(...)` instead of
//      calling ApiService directly, so a warm cache is served instantly.
//   2. `prefetchForDrawerAction` / `prefetchForNode` warm those same cache
//      entries when a node is added or when an existing workflow is loaded, so
//      by the time the user opens a node its dropdowns are already populated.
//
// Keys are namespaced under "flow" and kept stable so every consumer (and the
// prefetch) resolves to the same entry. Query functions return the same shape
// each panel previously stored in local state, to keep panel render code
// unchanged.

const FLOW = "flow" as const;

export const flowChannelsQuery = () => ({
  queryKey: [FLOW, "channels"],
  queryFn: async () => {
    const { data } = await ApiService.get<any>({
      url: "/workspaces/channels",
    });
    return data || {};
  },
});

export const flowTemplatesByTypeQuery = (type: MessageType) => ({
  queryKey: [FLOW, "templates", type],
  queryFn: async () => {
    const { data } = await ApiService.get<{ data: Template[] }>({
      url: `/templates?type=${type}&take=100&orderBy=updatedAt&orderType=desc`,
    });
    return data?.data || [];
  },
});

export const flowTrackerTemplatesQuery = () => ({
  queryKey: [FLOW, "templates", "custom_component"],
  queryFn: async () => {
    const {
      data: { data },
    } = await ApiService.get<{ data: Template[]; totalPages: number }>({
      url: `/templates?type=custom_component`,
    });
    return data || [];
  },
});

export const flowPlaceholdersQuery = () => ({
  queryKey: [FLOW, "placeholders"],
  queryFn: async () => {
    const { data } = await ApiService.get<Record<string, string | null>>({
      url: "/accounts/placeholders",
    });
    return data || {};
  },
});

export const flowAiAgentsQuery = () => ({
  queryKey: [FLOW, "ai-agents"],
  queryFn: async () => {
    const { data } = await ApiService.get<AiAgent[]>({
      url: "/ai-agents?includeArchived=true",
    });
    return data || [];
  },
});

export const flowLeadStatsMergeFieldsQuery = (
  channel: "email" | "sms" | "internal"
) => ({
  queryKey: [FLOW, "leadstats-merge-fields", channel],
  queryFn: async () => {
    const { data } = await ApiService.get<LeadStatsMergeField[]>({
      url: "/leadstats/merge-fields",
      options: {
        params: { channel },
      },
    });
    return data || [];
  },
});

export interface RicochetStatusOption {
  id: number | string;
  name: string;
  active?: number | boolean;
}

export interface RicochetTagOption {
  id: number;
  name: string;
  internalId?: number | null;
}

export const flowRicochetStatusesQuery = () => ({
  queryKey: [FLOW, "ricochet", "statuses"],
  queryFn: async () => {
    const { data } = await ApiService.get<RicochetStatusOption[]>({
      url: "/ricochet-data/statuses",
    });
    return data || [];
  },
});

export const flowRicochetTagsQuery = () => ({
  queryKey: [FLOW, "ricochet", "tags"],
  queryFn: async () => {
    const { data } = await ApiService.get<RicochetTagOption[]>({
      url: "/ricochet-data/tags",
    });
    return data || [];
  },
});

export const flowGroupsQuery = () => ({
  queryKey: [FLOW, "groups"],
  queryFn: async () => {
    const {
      data: { data },
    } = await ApiService.get<{ data: any[] }>({ url: "/groups" });
    return data;
  },
});

export const flowInternalRecipientGroupsQuery = () => ({
  queryKey: [FLOW, "internal-recipient-groups"],
  queryFn: async () => {
    const {
      data: { data },
    } = await ApiService.get<{ data: InternalRecipientGroup[] }>({
      url: "/internal-recipient-groups?take=100",
    });
    return data || [];
  },
});

export const flowPossibleAttributesQuery = () => ({
  queryKey: [FLOW, "possible-attributes", ""],
  queryFn: async () => {
    const { data } = await ApiService.get<any>({
      url: "/contacts/possible-attributes?key=",
    });
    return data;
  },
});

export const flowAttributeTypesQuery = () => ({
  queryKey: [FLOW, "possible-attribute-types"],
  queryFn: async () => {
    const { data } = await ApiService.get<any>({
      url: "/contacts/possible-attribute-types",
    });
    return data;
  },
});

export const flowAttributeParametersQuery = () => ({
  queryKey: [FLOW, "possible-attribute-parameters"],
  queryFn: async () => {
    const { data } = await ApiService.get<any[]>({
      url: `/contacts/possible-attribute-parameters/`,
    });
    return data;
  },
});

export const flowInitialWorkflowsQuery = () => ({
  queryKey: [FLOW, "workflows", "initial"],
  queryFn: async () => {
    const { data } = await ApiService.get<{ data: any[]; totalPages: number }>({
      url: `/workflows?take=12&skip=0&search=&orderBy=latestSave&orderType=desc&filterStatuses=Active,Paused,Draft`,
    });
    return data;
  },
});

export const flowActivityHistoryQuery = (
  workflowId: string,
  page: number,
  pageSize: number
) => ({
  queryKey: [FLOW, "activity-history", workflowId, page],
  queryFn: async () => {
    const { data } = await ApiService.get<{
      activityEvents: any[];
      totalPages: number;
    }>({
      url: `/workflows/${workflowId}/changes?take=${pageSize}&skip=${
        (page - 1) * pageSize
      }`,
    });
    return data;
  },
});

// The set of attribute/group/workflow reference data a condition or filter
// builder loads on mount (Multisplit, User attribute, Wait until).
const prefetchFilterData = (queryClient: QueryClient) => {
  queryClient.prefetchQuery(flowGroupsQuery());
  queryClient.prefetchQuery(flowPossibleAttributesQuery());
  queryClient.prefetchQuery(flowAttributeTypesQuery());
  queryClient.prefetchQuery(flowAttributeParametersQuery());
  queryClient.prefetchQuery(flowInitialWorkflowsQuery());
};

const prefetchMessageData = (
  queryClient: QueryClient,
  messageType?: MessageType
) => {
  queryClient.prefetchQuery(flowChannelsQuery());
  if (messageType)
    queryClient.prefetchQuery(flowTemplatesByTypeQuery(messageType));
  if (messageType === MessageType.EMAIL) {
    queryClient.prefetchQuery(flowLeadStatsMergeFieldsQuery("email"));
  }
  if (messageType === MessageType.SMS) {
    queryClient.prefetchQuery(flowLeadStatsMergeFieldsQuery("sms"));
  }
  if (messageType === MessageType.SLACK) {
    queryClient.prefetchQuery(flowLeadStatsMergeFieldsQuery("internal"));
  }
};

const drawerActionToMessageType: Partial<Record<DrawerAction, MessageType>> = {
  [DrawerAction.EMAIL]: MessageType.EMAIL,
  [DrawerAction.INTERNAL_EMAIL]: MessageType.EMAIL,
  [DrawerAction.SMS]: MessageType.SMS,
  [DrawerAction.SLACK]: MessageType.SLACK,
  [DrawerAction.PUSH]: MessageType.PUSH,
  [DrawerAction.WEBHOOK]: MessageType.WEBHOOK,
  [DrawerAction.IN_APP_MESSAGE]: MessageType.IN_APP,
};

// Warm the cache for a node the moment it's dropped onto the canvas, using the
// drawer action the user dragged.
export const prefetchForDrawerAction = (
  queryClient: QueryClient,
  action: DrawerAction
) => {
  switch (action) {
    case DrawerAction.EMAIL:
    case DrawerAction.SMS:
    case DrawerAction.SLACK:
    case DrawerAction.PUSH:
    case DrawerAction.WEBHOOK:
    case DrawerAction.IN_APP_MESSAGE:
      prefetchMessageData(queryClient, drawerActionToMessageType[action]);
      break;
    case DrawerAction.AI_RESPONSE:
      queryClient.prefetchQuery(flowChannelsQuery());
      queryClient.prefetchQuery(flowAiAgentsQuery());
      queryClient.prefetchQuery(flowPlaceholdersQuery());
      break;
    case DrawerAction.TRACKER:
      queryClient.prefetchQuery(flowTrackerTemplatesQuery());
      break;
    case DrawerAction.RICOCHET:
      queryClient.prefetchQuery(flowRicochetStatusesQuery());
      queryClient.prefetchQuery(flowRicochetTagsQuery());
      break;
    case DrawerAction.MULTISPLIT:
    case DrawerAction.USER_ATTRIBUTE:
    case DrawerAction.WAIT_UNTIL:
      prefetchFilterData(queryClient);
      break;
    default:
      break;
  }
};

// Warm the cache for a node already present in a loaded workflow.
export const prefetchForNode = (
  queryClient: QueryClient,
  node: Node<NodeData>
) => {
  switch (node.data.type) {
    case NodeType.MESSAGE:
    case NodeType.PUSH:
      prefetchMessageData(
        queryClient,
        (node.data as MessageNodeData).template?.type
      );
      break;
    case NodeType.AI_RESPONSE:
      queryClient.prefetchQuery(flowChannelsQuery());
      queryClient.prefetchQuery(flowAiAgentsQuery());
      queryClient.prefetchQuery(flowPlaceholdersQuery());
      break;
    case NodeType.TRACKER:
      queryClient.prefetchQuery(flowTrackerTemplatesQuery());
      break;
    case NodeType.RICOCHET:
      queryClient.prefetchQuery(flowRicochetStatusesQuery());
      queryClient.prefetchQuery(flowRicochetTagsQuery());
      break;
    case NodeType.MULTISPLIT:
    case NodeType.WAIT_UNTIL:
      prefetchFilterData(queryClient);
      break;
    default:
      break;
  }
};

// Warm every node in a freshly loaded workflow.
export const prefetchForNodes = (
  queryClient: QueryClient,
  nodes: Node<NodeData>[]
) => {
  nodes.forEach((node) => prefetchForNode(queryClient, node));
};
